import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import MainLayout from '../../components/layout/MainLayout';
import cloudScanApi, {
    ScanResult,
    SecurityFinding,
    CostRecommendation,
    FinOpsRecommendation,
    FinOpsSummary,
    Severity,
    CloudProvider,
    FinOpsCategory,
} from '../../services/api/cloud-scan.api';
import toast from 'react-hot-toast';

// Severity badge colors
const getSeverityColor = (severity: Severity) => {
    switch (severity) {
        case 'critical':
            return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400 border-red-200 dark:border-red-800';
        case 'high':
            return 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400 border-orange-200 dark:border-orange-800';
        case 'medium':
            return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400 border-yellow-200 dark:border-yellow-800';
        case 'low':
            return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400 border-blue-200 dark:border-blue-800';
        case 'info':
            return 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400 border-gray-200 dark:border-gray-800';
    }
};

// Provider colors
const getProviderColor = (provider: CloudProvider) => {
    switch (provider) {
        case 'aws':
            return 'from-orange-500 to-yellow-500';
        case 'azure':
            return 'from-blue-500 to-cyan-500';
        case 'gcp':
            return 'from-green-500 to-blue-500';
    }
};

// Currency formatting helper - will be overridden in component
let tenantCurrency: 'USD' | 'BRL' = 'USD';

const formatCurrency = (value: number) => {
    const locale = tenantCurrency === 'BRL' ? 'pt-BR' : 'en-US';
    return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: tenantCurrency,
        minimumFractionDigits: 2,
    }).format(value);
};

const formatDuration = (ms: number | undefined) => {
    if (!ms || isNaN(ms)) return '0s';
    if (ms < 1000) return `${ms}ms`;
    if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
    return `${(ms / 60000).toFixed(1)}m`;
};

const formatTimestamp = (timestamp: string | number | undefined) => {
    if (!timestamp) return 'N/A';
    try {
        const date = new Date(timestamp);
        if (isNaN(date.getTime())) return 'N/A';
        return date.toLocaleString();
    } catch (error) {
        return 'N/A';
    }
};

export default function CloudScanDetailsPage() {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { t } = useTranslation(['cloudScan', 'common']);
    const user = useSelector((state: RootState) => state.auth.user);

    // Set tenant currency for formatting
    tenantCurrency = (user?.tenant as any)?.currency || 'USD';

    const [scanResult, setScanResult] = useState<ScanResult | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [activeTab, setActiveTab] = useState<'security' | 'cost' | 'finops'>('security');
    const [expandedFinding, setExpandedFinding] = useState<string | null>(null);
    const [hasPremiumAccess, setHasPremiumAccess] = useState(false);
    const [isPaid, setIsPaid] = useState(false);
    const [scanStatus, setScanStatus] = useState<'pending' | 'running' | 'completed' | 'failed'>('pending');
    const [pollCount, setPollCount] = useState(0);

    // Load scan details initially
    useEffect(() => {
        if (id) {
            loadScanDetails(id);
        }
    }, [id]);

    // Poll for scan status while running
    useEffect(() => {
        let pollInterval: NodeJS.Timeout | null = null;

        if (scanStatus === 'running' && id) {
            pollInterval = setInterval(() => {
                console.log(`[Polling] Checking scan status... (attempt ${pollCount + 1})`);
                setPollCount(prev => prev + 1);
                loadScanDetails(id, true); // silent poll
            }, 5000); // Poll every 5 seconds
        }

        return () => {
            if (pollInterval) {
                clearInterval(pollInterval);
            }
        };
    }, [scanStatus, id, pollCount]);

    const loadScanDetails = async (scanId: string, silentPoll = false) => {
        try {
            if (!silentPoll) {
                setLoading(true);
            }
            setError(null);
            const response = await cloudScanApi.getScan(scanId);

            console.log('=== FULL SCAN RESPONSE ===');
            console.log('Response:', JSON.stringify(response, null, 2));
            console.log('Status:', (response.data as any)?.status);

            // Check scan status from the API response
            const apiStatus = (response.data as any)?.status || 'completed';
            setScanStatus(apiStatus);

            // If scan is still running, don't update the result yet
            if (apiStatus === 'running' || apiStatus === 'pending') {
                if (!silentPoll) {
                    setLoading(false);
                }
                return;
            }

            if (response.success && response.data) {
                // Map API response to expected format
                const apiData: any = response.data;

                // Generate recommendations based on finding type
                const getRecommendation = (finding: any) => {
                    if (finding.recommendation) return finding.recommendation;

                    const type = finding.type || '';
                    if (type === 'open_port') {
                        if (finding.severity === 'critical') {
                            return t('cloudScan:recommendations.closePort', 'Feche imediatamente esta porta sensível ou restrinja o acesso apenas a IPs conhecidos usando regras de Security Group');
                        }
                        return t('cloudScan:recommendations.restrictPort', 'Restrinja o acesso desta porta apenas a IPs confiáveis usando regras de Security Group');
                    }
                    if (type === 'public_acl') {
                        return t('cloudScan:recommendations.removePublicAccess', 'Remova o acesso público deste bucket S3 e configure políticas de acesso apropriadas');
                    }
                    if (type === 'key_without_mfa') {
                        return t('cloudScan:recommendations.enableMFA', 'Habilite MFA (Multi-Factor Authentication) para este usuário IAM para aumentar a segurança');
                    }
                    if (type === 'old_key') {
                        return t('cloudScan:recommendations.rotateKey', 'Rotacione esta chave de acesso antiga e remova chaves não utilizadas');
                    }
                    return t('cloudScan:recommendations.default', 'Revise e corrija este problema de segurança');
                };

                // Transform security findings to expected format
                const securityFindings = (apiData.security_findings || []).map((finding: any, index: number) => ({
                    id: finding.id || `finding-${index}`,
                    title: finding.title || finding.resource || t('cloudScan:finding.securityIssue', `Problema de Segurança #${index + 1}`),
                    description: finding.description || t('cloudScan:finding.noDescription', 'Sem descrição disponível'),
                    severity: finding.severity || 'medium',
                    category: finding.category || finding.type || 'security',
                    resource: finding.resource || t('cloudScan:finding.unknown', 'Desconhecido'),
                    resourceType: finding.resourceType || finding.type || t('cloudScan:finding.unknown', 'Desconhecido'),
                    region: finding.region,
                    recommendation: getRecommendation(finding),
                    metadata: finding,
                }));

                // Generate cost recommendations based on type
                const getCostRecommendation = (rec: any) => {
                    if (rec.recommendation || rec.action) return rec.recommendation || rec.action;

                    const type = rec.type || '';
                    if (type.includes('idle') || type.includes('stopped')) {
                        return t('cloudScan:recommendations.stopIdleResource');
                    }
                    if (type.includes('snapshot') || type.includes('backup')) {
                        return t('cloudScan:recommendations.deleteOldSnapshot');
                    }
                    if (type.includes('volume') || type.includes('disk')) {
                        return t('cloudScan:recommendations.detachVolume');
                    }
                    if (type.includes('underutilized') || type.includes('rightsiz')) {
                        return t('cloudScan:recommendations.rightSize');
                    }
                    return t('cloudScan:recommendations.default', 'Revise esta oportunidade de otimização de custo');
                };

                // Transform cost recommendations to expected format
                const costRecommendations = (apiData.cost_recommendations || []).map((rec: any, index: number) => ({
                    id: rec.id || `cost-${index}`,
                    title: rec.title || rec.resource || t('cloudScan:finding.costIssue', `Otimização de Custo #${index + 1}`),
                    description: rec.description || t('cloudScan:finding.noDescription', 'Sem descrição disponível'),
                    category: rec.category || rec.type || 'cost',
                    resource: rec.resource || t('cloudScan:finding.unknown', 'Desconhecido'),
                    resourceType: rec.resourceType || rec.type || t('cloudScan:finding.unknown', 'Desconhecido'),
                    region: rec.region,
                    estimatedMonthlySavings: rec.estimatedMonthlySavings || rec.estimated_monthly_savings || 0,
                    recommendation: getCostRecommendation(rec),
                    metadata: rec,
                }));

                // Transform FinOps recommendations if present
                const finopsRecommendations = (apiData.finops_recommendations || []).map((rec: any, index: number) => ({
                    id: rec.id || `finops-${index}`,
                    type: rec.type,
                    category: rec.category,
                    severity: rec.severity || 'medium',
                    resource: rec.resource,
                    title: rec.title,
                    description: rec.description,
                    currentMonthlyCost: rec.currentMonthlyCost || 0,
                    estimatedMonthlySavings: rec.estimatedMonthlySavings || 0,
                    estimatedAnnualSavings: rec.estimatedAnnualSavings || 0,
                    savingsPercentage: rec.savingsPercentage || 0,
                    recommendation: rec.recommendation,
                    implementationEffort: rec.implementationEffort || 'medium',
                    riskLevel: rec.riskLevel || 'low',
                    metadata: rec,
                }));

                // Default FinOps summary if not present
                const defaultFinOpsSummary: FinOpsSummary = {
                    totalCurrentMonthlyCost: 0,
                    totalEstimatedMonthlySavings: 0,
                    totalEstimatedAnnualSavings: 0,
                    savingsPercentage: 0,
                    recommendationCount: 0,
                    byCategory: {
                        reservedInstances: { count: 0, savings: 0 },
                        rightSizing: { count: 0, savings: 0 },
                        storageOptimization: { count: 0, savings: 0 },
                        idleResources: { count: 0, savings: 0 },
                        costAnomalies: { count: 0, savings: 0 },
                    },
                    topOpportunities: [],
                    lastMonthCost: 0,
                    costTrend: 0,
                    costHistory: [],
                    topServices: [],
                };

                const finopsSummary = apiData.finops_summary || defaultFinOpsSummary;

                console.log('=== FINOPS DEBUG ===');
                console.log('apiData.finops_summary:', apiData.finops_summary);
                console.log('apiData.finops_recommendations:', apiData.finops_recommendations);
                console.log('finopsSummary (final):', finopsSummary);
                console.log('finopsRecommendations (final):', finopsRecommendations);

                // The API returns security_findings and cost_recommendations at root level
                // We need to transform it to the format the UI expects
                const scanData: ScanResult = {
                    provider: apiData.provider || 'aws',
                    scanType: apiData.scanType || 'full',
                    timestamp: apiData.timestamp || new Date().toISOString(),
                    duration: apiData.duration || 0,
                    security: {
                        findings: securityFindings,
                        findingsCount: apiData.summary?.totalSecurityFindings || 0,
                    },
                    cost: {
                        recommendations: costRecommendations,
                        recommendationsCount: apiData.summary?.totalCostRecommendations || 0,
                    },
                    finops: {
                        recommendations: finopsRecommendations,
                        summary: finopsSummary,
                    },
                    report: {
                        summary: apiData.summary || {
                            totalSecurityFindings: 0,
                            totalCostRecommendations: 0,
                            estimatedTotalMonthlySavings: 0,
                            estimatedMonthlySavings: 0,
                        },
                    },
                    errors: apiData.errors || [],
                };

                console.log('=== SETTING SCAN RESULT ===');
                console.log('Transformed scan data:', JSON.stringify(scanData, null, 2));
                setScanResult(scanData);
                setHasPremiumAccess(response.hasPremiumAccess || false);
                setIsPaid(response.isPaid || response.effectiveIsPaid || false);
            } else {
                console.error('Response was not successful or no data');
                setError('Failed to load scan details');
            }
        } catch (err: any) {
            console.error('Failed to load scan:', err);
            console.error('Error details:', err.response?.data);
            setError(err.message || 'Failed to load scan details');
        } finally {
            setLoading(false);
        }
    };

    const handlePurchaseScan = async () => {
        if (!id) return;

        if (!confirm(t('common:confirmPurchase', 'Are you sure you want to purchase this scan report for $9.99?'))) {
            return;
        }

        try {
            const response = await cloudScanApi.purchaseScan(id);
            if (response.success) {
                // Reload scan data
                await loadScanDetails(id);
                toast.success(t('common:purchaseSuccess', 'Scan report purchased successfully!'), {
                    duration: 3000,
                    icon: '✅',
                });
            }
        } catch (error: any) {
            console.error('Failed to purchase scan:', error);
            toast.error(error.response?.data?.error || t('common:purchaseError', 'Failed to purchase scan report'), {
                duration: 5000,
                icon: '❌',
            });
        }
    };

    const handleExportExcel = async () => {
        if (!id) return;

        try {
            const response = await fetch(`${import.meta.env.VITE_API_URL}/v1/cloud-scan/scans/${id}/export/excel`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`,
                },
            });

            if (!response.ok) {
                // Try to parse error as JSON, fallback to text
                let errorMessage = 'Failed to export Excel';
                try {
                    const contentType = response.headers.get('content-type');
                    if (contentType && contentType.includes('application/json')) {
                        const error = await response.json();
                        errorMessage = error.error || error.message || errorMessage;
                    } else {
                        const text = await response.text();
                        errorMessage = text || `HTTP ${response.status}: ${response.statusText}`;
                    }
                } catch (parseError) {
                    errorMessage = `HTTP ${response.status}: ${response.statusText}`;
                }
                throw new Error(errorMessage);
            }

            // Get filename from Content-Disposition header or create default
            const contentDisposition = response.headers.get('Content-Disposition');
            const filenameMatch = contentDisposition?.match(/filename="(.+)"/);
            const filename = filenameMatch ? filenameMatch[1] : `cloud-scan-${scanResult?.provider}-${Date.now()}.xlsx`;

            // Create blob and download
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
        } catch (error: any) {
            console.error('Failed to export Excel:', error);
            toast.error(error.message || t('cloudScan:errors.exportFailed', 'Failed to export report'), {
                duration: 5000,
                icon: '❌',
            });
        }
    };

    const handleExportPDF = async () => {
        if (!id) return;

        try {
            const response = await fetch(`${import.meta.env.VITE_API_URL}/v1/cloud-scan/scans/${id}/export/pdf`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`,
                },
            });

            if (!response.ok) {
                // Try to parse error as JSON, fallback to text
                let errorMessage = 'Failed to export PDF';
                try {
                    const contentType = response.headers.get('content-type');
                    if (contentType && contentType.includes('application/json')) {
                        const error = await response.json();
                        errorMessage = error.error || error.message || errorMessage;
                    } else {
                        const text = await response.text();
                        errorMessage = text || `HTTP ${response.status}: ${response.statusText}`;
                    }
                } catch (parseError) {
                    errorMessage = `HTTP ${response.status}: ${response.statusText}`;
                }
                throw new Error(errorMessage);
            }

            // Get filename from Content-Disposition header or create default
            const contentDisposition = response.headers.get('Content-Disposition');
            const filenameMatch = contentDisposition?.match(/filename="(.+)"/);
            const filename = filenameMatch ? filenameMatch[1] : `cloud-scan-${scanResult?.provider}-${Date.now()}.pdf`;

            // Create blob and download
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
        } catch (error: any) {
            console.error('Failed to export PDF:', error);
            toast.error(error.message || t('cloudScan:errors.exportFailed', 'Failed to export report'), {
                duration: 5000,
                icon: '❌',
            });
        }
    };

    if (loading && scanStatus !== 'running') {
        return (
            <MainLayout>
                <div className="flex items-center justify-center py-12">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
                </div>
            </MainLayout>
        );
    }

    // Show running state UI
    if (scanStatus === 'running' || scanStatus === 'pending') {
        return (
            <MainLayout>
                <div className="max-w-2xl mx-auto py-12 px-4">
                    <button
                        onClick={() => navigate('/cloud-scan')}
                        className="mb-8 flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors"
                    >
                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                        </svg>
                        {t('actions.newScan', 'Back to Cloud Scan')}
                    </button>

                    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8 text-center border border-gray-100 dark:border-gray-700">
                        {/* Animated cloud scan icon */}
                        <div className="relative mx-auto w-24 h-24 mb-6">
                            <div className="absolute inset-0 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 opacity-20 animate-ping"></div>
                            <div className="absolute inset-2 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 opacity-40 animate-pulse"></div>
                            <div className="absolute inset-4 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center">
                                <svg className="w-10 h-10 text-white animate-pulse" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 15a4 4 0 004 4h9a5 5 0 10-.1-9.999 5.002 5.002 0 10-9.78 2.096A4.001 4.001 0 003 15z" />
                                </svg>
                            </div>
                        </div>

                        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-3">
                            {t('scanning.title', 'Scanning...')}
                        </h2>
                        <p className="text-gray-600 dark:text-gray-400 mb-8">
                            {t('scanning.description', 'Analyzing your cloud infrastructure for security vulnerabilities and cost optimization opportunities.')}
                        </p>

                        {/* Progress steps */}
                        <div className="space-y-4 text-left max-w-sm mx-auto">
                            {[
                                { key: 'connecting', label: t('scanning.steps.connecting', 'Connecting to cloud provider') },
                                { key: 'scanning', label: t('scanning.steps.scanning', 'Scanning resources') },
                                { key: 'analyzing', label: t('scanning.steps.analyzing', 'Analyzing findings') },
                                { key: 'generating', label: t('scanning.steps.generating', 'Generating report') },
                            ].map((step, index) => (
                                <div key={step.key} className="flex items-center gap-3">
                                    <div className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center ${
                                        index < Math.min(Math.floor(pollCount / 2), 3)
                                            ? 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400'
                                            : index === Math.min(Math.floor(pollCount / 2), 3)
                                                ? 'bg-indigo-100 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 animate-pulse'
                                                : 'bg-gray-100 dark:bg-gray-700 text-gray-400'
                                    }`}>
                                        {index < Math.min(Math.floor(pollCount / 2), 3) ? (
                                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                            </svg>
                                        ) : (
                                            <span className="text-xs font-medium">{index + 1}</span>
                                        )}
                                    </div>
                                    <span className={`text-sm ${
                                        index <= Math.min(Math.floor(pollCount / 2), 3)
                                            ? 'text-gray-900 dark:text-white font-medium'
                                            : 'text-gray-500 dark:text-gray-400'
                                    }`}>
                                        {step.label}
                                    </span>
                                </div>
                            ))}
                        </div>

                        <div className="mt-8 pt-6 border-t border-gray-100 dark:border-gray-700">
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                                {t('scanning.autoRefresh', 'This page will automatically update when the scan is complete.')}
                            </p>
                        </div>
                    </div>
                </div>
            </MainLayout>
        );
    }

    if (error || !scanResult) {
        return (
            <MainLayout>
                <div className="text-center py-12">
                    <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                        {error || 'Scan not found'}
                    </h2>
                    <button
                        onClick={() => navigate('/cloud-scan')}
                        className="px-4 py-2 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700"
                    >
                        ← Back to Cloud Scan
                    </button>
                </div>
            </MainLayout>
        );
    }

    const effectiveIsPaid = isPaid || hasPremiumAccess;

    console.log('=== EXTRACTING FINDINGS ===');
    console.log('scanResult.security:', scanResult.security);
    console.log('scanResult.cost:', scanResult.cost);
    console.log('scanResult.report:', scanResult.report);

    // Extract findings with proper validation
    const securityFindings = Array.isArray(scanResult.security?.findings)
        ? scanResult.security.findings as SecurityFinding[]
        : [];

    const costRecommendations = Array.isArray(scanResult.cost?.recommendations)
        ? scanResult.cost.recommendations as CostRecommendation[]
        : [];

    console.log('=== EXTRACTED DATA ===');
    console.log('Security findings count:', securityFindings.length);
    console.log('Security findings:', securityFindings);
    console.log('Cost recommendations count:', costRecommendations.length);
    console.log('Cost recommendations:', costRecommendations);
    console.log('Is paid:', effectiveIsPaid);
    console.log('Has premium access:', hasPremiumAccess);

    return (
        <MainLayout>
            {/* Header */}
            <div className="mb-8">
                <button
                    onClick={() => navigate('/cloud-scan')}
                    className="mb-4 flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors"
                >
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                    {t('actions.newScan', 'Back to Cloud Scan')}
                </button>

                <div className="flex items-start justify-between flex-wrap gap-4">
                    <div>
                        <div className="flex items-center gap-3 mb-2">
                            <div className={`flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br ${scanResult.provider ? getProviderColor(scanResult.provider) : 'from-indigo-500 to-purple-600'} shadow-lg`}>
                                <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 15a4 4 0 004 4h9a5 5 0 10-.1-9.999 5.002 5.002 0 10-9.78 2.096A4.001 4.001 0 003 15z" />
                                </svg>
                            </div>
                            <div>
                                <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
                                    {scanResult.provider?.toUpperCase() || 'CLOUD'} {t('results.title', 'Scan Results')}
                                </h1>
                                <p className="text-gray-600 dark:text-gray-400">
                                    {t('results.completedIn')} {formatDuration(scanResult.duration)} • {formatTimestamp(scanResult.timestamp)}
                                </p>
                            </div>
                        </div>
                        {(isPaid || hasPremiumAccess) && (
                            <div className="flex gap-2 mt-2">
                                {isPaid && (
                                    <span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400">
                                        ✓ Paid
                                    </span>
                                )}
                                {hasPremiumAccess && (
                                    <span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400">
                                        💎 Premium Access
                                    </span>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Export Buttons (only show if paid or premium access) */}
                    {effectiveIsPaid && (
                        <div className="flex gap-3">
                            <button
                                onClick={handleExportExcel}
                                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-green-600 text-white hover:bg-green-700 transition-colors shadow-md hover:shadow-lg"
                            >
                                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                </svg>
                                <span className="font-medium">Excel</span>
                            </button>
                            <button
                                onClick={handleExportPDF}
                                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-red-600 text-white hover:bg-red-700 transition-colors shadow-md hover:shadow-lg"
                            >
                                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                                </svg>
                                <span className="font-medium">PDF</span>
                            </button>
                        </div>
                    )}
                </div>
            </div>

            {/* Summary Cards */}
            <div className="mb-8 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
                <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-800 dark:bg-gray-900">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                                {t('results.summary.securityIssues')}
                            </p>
                            <p className="mt-2 text-3xl font-bold text-gray-900 dark:text-white">
                                {scanResult.report?.summary?.totalSecurityFindings || 0}
                            </p>
                        </div>
                        <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-red-100 dark:bg-red-900/20">
                            <svg className="h-6 w-6 text-red-600 dark:text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                            </svg>
                        </div>
                    </div>
                </div>

                <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-800 dark:bg-gray-900">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                                {t('results.summary.criticalIssues')}
                            </p>
                            <p className="mt-2 text-3xl font-bold text-gray-900 dark:text-white">
                                {scanResult.report?.summary?.severityBreakdown?.critical || 0}
                            </p>
                        </div>
                        <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-red-100 dark:bg-red-900/20">
                            <svg className="h-6 w-6 text-red-600 dark:text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                        </div>
                    </div>
                </div>

                <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-800 dark:bg-gray-900">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                                {t('results.summary.costOptimizations')}
                            </p>
                            <p className="mt-2 text-3xl font-bold text-gray-900 dark:text-white">
                                {scanResult.report?.summary?.totalCostRecommendations || 0}
                            </p>
                        </div>
                        <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-green-100 dark:bg-green-900/20">
                            <svg className="h-6 w-6 text-green-600 dark:text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                        </div>
                    </div>
                </div>

                <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-800 dark:bg-gray-900">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                                {t('results.summary.potentialSavings')}
                            </p>
                            <p className="mt-2 text-3xl font-bold text-gray-900 dark:text-white">
                                {formatCurrency(scanResult.report?.summary?.estimatedMonthlySavings || 0)}
                            </p>
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                                {t('results.summary.perMonth')}
                            </p>
                        </div>
                        <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-green-100 dark:bg-green-900/20">
                            <svg className="h-6 w-6 text-green-600 dark:text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                            </svg>
                        </div>
                    </div>
                </div>
            </div>

            {/* Tabs */}
            <div className="mb-6">
                <div className="border-b border-gray-200 dark:border-gray-700">
                    <nav className="-mb-px flex space-x-8">
                        <button
                            onClick={() => setActiveTab('security')}
                            className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                                activeTab === 'security'
                                    ? 'border-indigo-500 text-indigo-600 dark:text-indigo-400'
                                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                            }`}
                        >
                            🛡️ {t('results.tabs.security')}
                            <span className="ml-2 px-2 py-0.5 rounded-full bg-gray-100 dark:bg-gray-800 text-xs">
                                {securityFindings.length}
                            </span>
                        </button>
                        <button
                            onClick={() => setActiveTab('cost')}
                            className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                                activeTab === 'cost'
                                    ? 'border-indigo-500 text-indigo-600 dark:text-indigo-400'
                                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                            }`}
                        >
                            💰 {t('results.tabs.cost')}
                            <span className="ml-2 px-2 py-0.5 rounded-full bg-gray-100 dark:bg-gray-800 text-xs">
                                {costRecommendations.length}
                            </span>
                        </button>
                        <button
                            onClick={() => setActiveTab('finops')}
                            className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                                activeTab === 'finops'
                                    ? 'border-indigo-500 text-indigo-600 dark:text-indigo-400'
                                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                            }`}
                        >
                            📊 FinOps
                            <span className="ml-2 px-2 py-0.5 rounded-full bg-gray-100 dark:bg-gray-800 text-xs">
                                {scanResult.finops?.recommendations?.length || 0}
                            </span>
                        </button>
                    </nav>
                </div>
            </div>

            {/* Findings List */}
            {activeTab === 'security' && (
                <div className="space-y-4">
                    {securityFindings.length === 0 ? (
                        <div className="rounded-xl border border-gray-200 bg-white p-12 text-center dark:border-gray-800 dark:bg-gray-900">
                            <div className="flex justify-center mb-4">
                                <div className="flex h-16 w-16 items-center justify-center rounded-full bg-green-100 dark:bg-green-900/20">
                                    <svg className="h-8 w-8 text-green-600 dark:text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                </div>
                            </div>
                            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                                {t('results.noSecurityIssues.title', 'No Security Issues Found')}
                            </h3>
                            <p className="text-gray-600 dark:text-gray-400">
                                {t('results.noSecurityIssues.description', 'Your cloud infrastructure passed all security checks!')}
                            </p>
                        </div>
                    ) : (
                        securityFindings.map((finding, index) => (
                            <div
                                key={index}
                                className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-800 dark:bg-gray-900"
                            >
                                <div className="flex items-start justify-between gap-4">
                                    <div className="flex-1">
                                        <div className="flex items-center gap-3 mb-2">
                                            <span className={`px-3 py-1 rounded-full text-xs font-semibold border ${getSeverityColor(finding.severity)}`}>
                                                {finding.severity.toUpperCase()}
                                            </span>
                                            <h4 className="text-lg font-semibold text-gray-900 dark:text-white">
                                                {finding.title}
                                            </h4>
                                        </div>
                                        <p className="text-gray-700 dark:text-gray-300 mb-4">
                                            {finding.description}
                                        </p>
                                        {finding.recommendation && (
                                            <div className="rounded-lg bg-blue-50 dark:bg-blue-900/10 p-4">
                                                <p className="text-sm font-semibold text-blue-900 dark:text-blue-300 mb-1">
                                                    {t('results.finding.recommendation')}
                                                </p>
                                                <p className="text-sm text-blue-800 dark:text-blue-400">
                                                    {finding.recommendation}
                                                </p>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))
                    )}
                </div>
            )}

            {activeTab === 'cost' && (
                <div className="space-y-4">
                    {costRecommendations.length === 0 ? (
                        <div className="rounded-xl border border-gray-200 bg-white p-12 text-center dark:border-gray-800 dark:bg-gray-900">
                            <div className="flex justify-center mb-4">
                                <div className="flex h-16 w-16 items-center justify-center rounded-full bg-green-100 dark:bg-green-900/20">
                                    <svg className="h-8 w-8 text-green-600 dark:text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                </div>
                            </div>
                            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                                {t('results.noCostOptimizations.title', 'No Cost Optimizations Needed')}
                            </h3>
                            <p className="text-gray-600 dark:text-gray-400">
                                {t('results.noCostOptimizations.description', 'Your cloud infrastructure is already cost-optimized!')}
                            </p>
                        </div>
                    ) : (
                        costRecommendations.map((recommendation, index) => (
                            <div
                                key={index}
                                className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-800 dark:bg-gray-900"
                            >
                                <div className="flex items-start justify-between gap-4">
                                    <div className="flex-1">
                                        <div className="flex items-center gap-3 mb-2">
                                            <span className="px-3 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400">
                                                {t('results.finding.save')} {formatCurrency(recommendation.estimatedMonthlySavings || 0)}/mo
                                            </span>
                                            <h4 className="text-lg font-semibold text-gray-900 dark:text-white">
                                                {recommendation.title}
                                            </h4>
                                        </div>
                                        <p className="text-gray-700 dark:text-gray-300 mb-4">
                                            {recommendation.description}
                                        </p>
                                        {recommendation.recommendation && (
                                            <div className="rounded-lg bg-green-50 dark:bg-green-900/10 p-4">
                                                <p className="text-sm font-semibold text-green-900 dark:text-green-300 mb-1">
                                                    {t('results.finding.recommendation')}
                                                </p>
                                                <p className="text-sm text-green-800 dark:text-green-400">
                                                    {recommendation.recommendation}
                                                </p>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))
                    )}
                </div>
            )}

            {activeTab === 'finops' && scanResult.finops?.summary && (
                <div className="space-y-6">
                    {/* Main Cost Overview - Hero Section */}
                    <div className="rounded-2xl bg-gradient-to-br from-slate-900 to-slate-800 p-8 text-white shadow-xl">
                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                            {/* Current Month Cost */}
                            <div className="text-center lg:text-left">
                                <p className="text-slate-400 text-sm font-medium mb-1">{t('results.finops.currentCost', 'Custo Mensal Atual')}</p>
                                <p className="text-4xl font-bold">{formatCurrency(scanResult.finops.summary.totalCurrentMonthlyCost)}</p>
                                {scanResult.finops.summary.costTrend !== undefined && scanResult.finops.summary.costTrend !== 0 && (
                                    <div className={`inline-flex items-center mt-2 px-3 py-1 rounded-full text-sm font-medium ${scanResult.finops.summary.costTrend > 0 ? 'bg-red-500/20 text-red-300' : 'bg-green-500/20 text-green-300'}`}>
                                        {scanResult.finops.summary.costTrend > 0 ? '↑' : '↓'} {Math.abs(scanResult.finops.summary.costTrend).toFixed(1)}% {t('results.finops.vsLastMonth', 'vs mês anterior')}
                                    </div>
                                )}
                            </div>
                            {/* Potential Savings */}
                            <div className="text-center border-l border-r border-slate-700 px-8">
                                <p className="text-slate-400 text-sm font-medium mb-1">{t('results.finops.monthlySavings', 'Economia Potencial Mensal')}</p>
                                <p className="text-4xl font-bold text-emerald-400">{formatCurrency(scanResult.finops.summary.totalEstimatedMonthlySavings)}</p>
                                <p className="text-slate-400 text-sm mt-1">{formatCurrency(scanResult.finops.summary.totalEstimatedAnnualSavings)}{t('results.finops.perYear', '/ano')}</p>
                            </div>
                            {/* Savings Percentage */}
                            <div className="text-center lg:text-right">
                                <p className="text-slate-400 text-sm font-medium mb-1">{t('results.finops.savingsPercentage', 'Potencial de Economia')}</p>
                                <p className="text-4xl font-bold text-amber-400">{scanResult.finops.summary.savingsPercentage.toFixed(1)}%</p>
                                <p className="text-slate-400 text-sm mt-1">{scanResult.finops.summary.recommendationCount} {t('results.finops.recommendations', 'recomendações')}</p>
                            </div>
                        </div>
                    </div>

                    {/* Cost History Chart & Top Services */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        {/* Cost History - Last 6 Months */}
                        <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-800 dark:bg-gray-900">
                            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                                <svg className="w-5 h-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                                </svg>
                                {t('results.finops.costHistory', 'Histórico de Custos (6 meses)')}
                            </h3>
                            {scanResult.finops.summary.costHistory && scanResult.finops.summary.costHistory.length > 0 ? (
                                <div className="space-y-3">
                                    {(() => {
                                        const maxCost = Math.max(...scanResult.finops.summary.costHistory!.map(h => h.cost), 1);
                                        return scanResult.finops.summary.costHistory!.map((item, index) => (
                                            <div key={index} className="flex items-center gap-3">
                                                <span className="text-xs text-gray-500 dark:text-gray-400 w-20">
                                                    {new Date(item.month).toLocaleDateString('pt-BR', { month: 'short', year: '2-digit' })}
                                                </span>
                                                <div className="flex-1 h-6 bg-gray-100 dark:bg-gray-800 rounded-full overflow-hidden">
                                                    <div
                                                        className="h-full bg-gradient-to-r from-blue-500 to-indigo-500 rounded-full transition-all duration-500"
                                                        style={{ width: `${(item.cost / maxCost) * 100}%` }}
                                                    />
                                                </div>
                                                <span className="text-sm font-semibold text-gray-900 dark:text-white w-24 text-right">
                                                    {formatCurrency(item.cost)}
                                                </span>
                                            </div>
                                        ));
                                    })()}
                                </div>
                            ) : (
                                <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                                    <svg className="w-12 h-12 mx-auto mb-3 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                                    </svg>
                                    <p>{t('results.finops.noHistoryData', 'Sem dados de histórico disponíveis')}</p>
                                    <p className="text-xs mt-1">{t('results.finops.enableCostExplorer', 'Habilite o AWS Cost Explorer para ver o histórico')}</p>
                                </div>
                            )}
                        </div>

                        {/* Top Services by Cost */}
                        <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-800 dark:bg-gray-900">
                            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                                <svg className="w-5 h-5 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z" />
                                </svg>
                                {t('results.finops.topServices', 'Top Serviços por Custo')}
                            </h3>
                            {scanResult.finops.summary.topServices && scanResult.finops.summary.topServices.length > 0 ? (
                                <div className="space-y-3">
                                    {(() => {
                                        const maxServiceCost = Math.max(...scanResult.finops.summary.topServices!.map(s => s.cost), 1);
                                        const colors = ['bg-purple-500', 'bg-indigo-500', 'bg-blue-500', 'bg-cyan-500', 'bg-teal-500', 'bg-emerald-500', 'bg-green-500', 'bg-lime-500', 'bg-yellow-500', 'bg-orange-500'];
                                        return scanResult.finops.summary.topServices!.slice(0, 8).map((service, index) => (
                                            <div key={index} className="flex items-center gap-3">
                                                <div className={`w-2 h-2 rounded-full ${colors[index % colors.length]}`} />
                                                <span className="text-sm text-gray-700 dark:text-gray-300 flex-1 truncate" title={service.service}>
                                                    {service.service.replace('Amazon ', '').replace('AWS ', '')}
                                                </span>
                                                <div className="w-24 h-2 bg-gray-100 dark:bg-gray-800 rounded-full overflow-hidden">
                                                    <div
                                                        className={`h-full ${colors[index % colors.length]} rounded-full`}
                                                        style={{ width: `${(service.cost / maxServiceCost) * 100}%` }}
                                                    />
                                                </div>
                                                <span className="text-sm font-semibold text-gray-900 dark:text-white w-20 text-right">
                                                    {formatCurrency(service.cost)}
                                                </span>
                                            </div>
                                        ));
                                    })()}
                                </div>
                            ) : (
                                <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                                    <svg className="w-12 h-12 mx-auto mb-3 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                                    </svg>
                                    <p>{t('results.finops.noServiceData', 'Sem dados de serviços disponíveis')}</p>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Category Breakdown - Compact Cards */}
                    <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-800 dark:bg-gray-900">
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                            <svg className="w-5 h-5 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 3.055A9.001 9.001 0 1020.945 13H11V3.055z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.488 9H15V3.512A9.025 9.025 0 0120.488 9z" />
                            </svg>
                            {t('results.finops.byCategory', 'Economia por Categoria')}
                        </h3>
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
                            {[
                                { key: 'reservedInstances', labelKey: 'reservedInstances', icon: '🎫', color: 'indigo' },
                                { key: 'rightSizing', labelKey: 'rightSizing', icon: '📐', color: 'emerald' },
                                { key: 'storageOptimization', labelKey: 'storageOptimization', icon: '💾', color: 'cyan' },
                                { key: 'idleResources', labelKey: 'idleResources', icon: '💤', color: 'orange' },
                                { key: 'costAnomalies', labelKey: 'costAnomalies', icon: '⚠️', color: 'red' },
                            ].map((cat) => {
                                const data = scanResult.finops!.summary!.byCategory[cat.key as keyof typeof scanResult.finops.summary.byCategory];
                                return (
                                    <div key={cat.key} className={`p-4 rounded-xl bg-${cat.color}-50 dark:bg-${cat.color}-900/20 border border-${cat.color}-100 dark:border-${cat.color}-800/30`}>
                                        <div className="flex items-center gap-2 mb-2">
                                            <span className="text-lg">{cat.icon}</span>
                                            <span className={`text-xs font-medium text-${cat.color}-600 dark:text-${cat.color}-400`}>{t(`results.finops.${cat.labelKey}`, cat.labelKey)}</span>
                                        </div>
                                        <p className={`text-xl font-bold text-${cat.color}-900 dark:text-${cat.color}-100`}>
                                            {formatCurrency(data.savings)}
                                        </p>
                                        <p className={`text-xs text-${cat.color}-600 dark:text-${cat.color}-400`}>
                                            {data.count} {t('results.finops.recommendations', 'recomendações')}
                                        </p>
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    {/* Recommendations List */}
                    {(!scanResult.finops?.recommendations || scanResult.finops.recommendations.length === 0) ? (
                        <div className="rounded-xl border border-gray-200 bg-white p-12 text-center dark:border-gray-800 dark:bg-gray-900">
                            <div className="flex justify-center mb-4">
                                <div className="flex h-20 w-20 items-center justify-center rounded-full bg-gradient-to-br from-green-100 to-emerald-100 dark:from-green-900/30 dark:to-emerald-900/30">
                                    <svg className="h-10 w-10 text-green-600 dark:text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                </div>
                            </div>
                            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                                {t('results.finops.noRecommendations', 'Nenhuma Recomendação FinOps')}
                            </h3>
                            <p className="text-gray-600 dark:text-gray-400 max-w-md mx-auto">
                                {t('results.finops.noRecommendationsDesc', 'Sua infraestrutura cloud já está bem otimizada em custos!')}
                            </p>
                        </div>
                    ) : (
                        <div>
                            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                                <svg className="w-5 h-5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                                </svg>
                                {t('results.finops.recommendationsList', 'Recomendações de Otimização')}
                            </h3>
                            <div className="space-y-4">
                                {scanResult.finops.recommendations.map((rec, index) => (
                                    <div
                                        key={index}
                                        className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-800 dark:bg-gray-900 hover:shadow-md transition-shadow"
                                    >
                                        <div className="flex items-start justify-between gap-4">
                                            <div className="flex-1">
                                                <div className="flex items-center gap-2 mb-3 flex-wrap">
                                                    <span className={`px-3 py-1 rounded-full text-xs font-semibold border ${getSeverityColor(rec.severity)}`}>
                                                        {rec.severity.toUpperCase()}
                                                    </span>
                                                    <span className="px-3 py-1 rounded-full text-xs font-semibold bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400">
                                                        {rec.category}
                                                    </span>
                                                </div>
                                                <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                                                    {rec.title}
                                                </h4>
                                                <p className="text-gray-700 dark:text-gray-300 mb-4">
                                                    {rec.description}
                                                </p>
                                                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                                                    <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-3">
                                                        <p className="text-xs text-gray-500 dark:text-gray-400">{t('results.finops.currentCost', 'Custo Atual')}</p>
                                                        <p className="font-bold text-gray-900 dark:text-white">{formatCurrency(rec.currentMonthlyCost)}<span className="text-xs font-normal">{t('results.finops.perMonth', '/mês')}</span></p>
                                                    </div>
                                                    <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-3">
                                                        <p className="text-xs text-green-600 dark:text-green-400">{t('results.finops.potentialSavings', 'Economia')}</p>
                                                        <p className="font-bold text-green-700 dark:text-green-300">{formatCurrency(rec.estimatedMonthlySavings)}<span className="text-xs font-normal">{t('results.finops.perMonth', '/mês')}</span></p>
                                                    </div>
                                                    <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-3">
                                                        <p className="text-xs text-gray-500 dark:text-gray-400">{t('results.finops.effort', 'Esforço')}</p>
                                                        <p className={`font-bold ${rec.implementationEffort === 'low' ? 'text-green-600' : rec.implementationEffort === 'medium' ? 'text-yellow-600' : 'text-red-600'}`}>
                                                            {t(`results.finops.${rec.implementationEffort}`, rec.implementationEffort)}
                                                        </p>
                                                    </div>
                                                    <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-3">
                                                        <p className="text-xs text-gray-500 dark:text-gray-400">{t('results.finops.risk', 'Risco')}</p>
                                                        <p className={`font-bold ${rec.riskLevel === 'low' ? 'text-green-600' : rec.riskLevel === 'medium' ? 'text-yellow-600' : 'text-red-600'}`}>
                                                            {t(`results.finops.${rec.riskLevel}`, rec.riskLevel)}
                                                        </p>
                                                    </div>
                                                </div>
                                                <div className="rounded-lg bg-gradient-to-r from-indigo-50 to-purple-50 dark:from-indigo-900/20 dark:to-purple-900/20 p-4 border border-indigo-100 dark:border-indigo-800/30">
                                                    <p className="text-sm font-semibold text-indigo-900 dark:text-indigo-300 mb-1 flex items-center gap-2">
                                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                                                        </svg>
                                                        {t('results.finding.recommendation', 'Recomendação')}
                                                    </p>
                                                    <p className="text-sm text-indigo-800 dark:text-indigo-400">
                                                        {rec.recommendation}
                                                    </p>
                                                </div>
                                            </div>
                                            <div className="hidden lg:flex flex-col items-center justify-center bg-green-50 dark:bg-green-900/20 rounded-xl p-4 min-w-[120px]">
                                                <p className="text-xs text-green-600 dark:text-green-400 font-medium">{t('results.finops.savingsPercent', '% Economia')}</p>
                                                <p className="text-3xl font-bold text-green-700 dark:text-green-300">{rec.savingsPercentage.toFixed(0)}%</p>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            )}
        </MainLayout>
    );
}
